/**
 * Socket.io 连接管理
 */
import io from 'socket.io-client';
import config from '@/config';
import TokenManager from '@/utils/auth';

class SocketManager {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  /**
   * 连接 Socket
   * @param {Object} options - 连接选项
   * @param {string} options.url - Socket 服务器地址（可选，默认使用 API 地址）
   * @param {Object} options.auth - 认证信息（可选）
   */
  connect(options = {}) {
    if (this.socket && this.isConnected) {
      console.warn('Socket 已经连接');
      return this.socket;
    }

    // 获取 Socket 服务器地址
    const socketUrl = options.url || config.apiBaseUrl || '';
    
    // 获取 token
    const token = TokenManager.getToken();
    
    // 连接配置
    const connectOptions = {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
      timeout: 20000,
      auth: {
        token: token,
        ...options.auth
      }
    };

    // 创建连接
    this.socket = io(socketUrl, connectOptions);

    // 连接成功
    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      console.log('Socket 连接成功', this.socket.id);
      
      if (options.onConnect) {
        options.onConnect(this.socket);
      }
    });

    // 连接错误
    this.socket.on('connect_error', (error) => {
      console.error('Socket 连接错误:', error);
      this.isConnected = false;
      
      if (options.onError) {
        options.onError(error);
      }
    });

    // 断开连接
    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      console.log('Socket 断开连接:', reason);
      
      if (options.onDisconnect) {
        options.onDisconnect(reason);
      }
    });

    // 重连尝试
    this.socket.on('reconnect_attempt', (attemptNumber) => {
      this.reconnectAttempts = attemptNumber;
      console.log(`Socket 重连尝试 ${attemptNumber}/${this.maxReconnectAttempts}`);
    });

    // 重连成功
    this.socket.on('reconnect', (attemptNumber) => {
      console.log('Socket 重连成功，尝试次数:', attemptNumber);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      if (options.onReconnect) {
        options.onReconnect(attemptNumber);
      }
    });

    // 重连失败
    this.socket.on('reconnect_failed', () => {
      console.error('Socket 重连失败，已达到最大重连次数');
      this.isConnected = false;
      
      if (options.onReconnectFailed) {
        options.onReconnectFailed();
      }
    });

    return this.socket;
  }

  /**
   * 断开连接
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      console.log('Socket 已断开连接');
    }
  }

  /**
   * 发送消息
   * @param {string} event - 事件名称
   * @param {*} data - 数据
   * @param {Function} callback - 回调函数（可选）
   */
  emit(event, data, callback) {
    if (!this.socket || !this.isConnected) {
      console.error('Socket 未连接，无法发送消息');
      if (callback) {
        callback(new Error('Socket 未连接'));
      }
      return;
    }

    this.socket.emit(event, data, callback);
  }

  /**
   * 监听事件
   * @param {string} event - 事件名称
   * @param {Function} callback - 回调函数
   */
  on(event, callback) {
    if (!this.socket) {
      console.error('Socket 未初始化，无法监听事件');
      return;
    }

    this.socket.on(event, callback);
  }

  /**
   * 取消监听事件
   * @param {string} event - 事件名称
   * @param {Function} callback - 回调函数（可选）
   */
  off(event, callback) {
    if (!this.socket) {
      return;
    }

    if (callback) {
      this.socket.off(event, callback);
    } else {
      this.socket.off(event);
    }
  }

  /**
   * 获取 Socket 实例
   */
  getSocket() {
    return this.socket;
  }

  /**
   * 获取连接状态
   */
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      socketId: this.socket?.id,
      reconnectAttempts: this.reconnectAttempts
    };
  }
}

// 创建单例
const socketManager = new SocketManager();

export default socketManager;

